import { cn } from 'component-library';
import { useState } from 'react';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { processFactsData } from '@/utils/processFactsData';

import { ChartElementConfig } from './CompositeGraph';

type DataProcessorProps = {
  data: PlotReportFactElement[];
  groupByLabel?: string;
  countFromLabel?: string | null;
  filterByLabelName?: string;
  filterByLabelValue?: string;
  filterByLabels?: Record<string, string>[];
  dateGroupingFormat?: 'yyyy' | 'mm-yyyy';
  transposeCategoriesToRows?: boolean;
  keepZeroCount?: boolean;
  chartConfig: ChartElementConfig[];
  legendItems?: ChartLegendBarItem[];
  allowLegendFiltering?: boolean;
  selectedBenchmark?: string | null;
  customBenchmarkFact?: string;
  [key: string]: unknown;
};

export const DataProcessor = ({
  data,
  groupByLabel,
  countFromLabel,
  filterByLabelName,
  filterByLabelValue,
  filterByLabels,
  dateGroupingFormat,
  transposeCategoriesToRows,
  keepZeroCount,
  chartConfig,
  legendItems,
  allowLegendFiltering,
  selectedBenchmark,
  customBenchmarkFact,
  ...delegated
}: DataProcessorProps) => {
  const organizationId = useOrganizationId();
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, customBenchmarkFact ?? '');
  const activeCustomBenchmark =
    selectedBenchmark && customBenchmarkFact
      ? customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark)
      : undefined;

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const processedData = processFactsData({
    facts: data ?? [],
    groupByLabel,
    countFromLabel,
    filterByLabelName,
    filterByLabelValue,
    dateGroupingFormat,
    transposeCategoriesToRows,
    keepZeroCount,
    filterByLabels,
  });

  const handleLegendClick = (dataKey: string) => {
    setSelectedCategories((prev) => {
      const next = prev.includes(dataKey) ? prev.filter((c) => c !== dataKey) : [...prev, dataKey];
      return next.length === chartConfig.length ? [] : next;
    });
  };

  const resolvedChartConfig =
    allowLegendFiltering && selectedCategories.length > 0
      ? chartConfig.filter((item) => selectedCategories.includes(item.dataKey))
      : chartConfig;

  const resolvedLegendItems =
    allowLegendFiltering && legendItems
      ? legendItems.map((item, index) => {
          if (index === 0) {
            return {
              ...item,
              onClick: () => setSelectedCategories([]),
              className: cn(item.className, { 'bg-neutral-hover': selectedCategories.length === 0 }),
            };
          }
          const dataKey = chartConfig[index - 1]?.dataKey;
          return {
            ...item,
            onClick: () => dataKey && handleLegendClick(dataKey),
            className: cn(item.className, { 'bg-neutral-hover': !!dataKey && selectedCategories.includes(dataKey) }),
          };
        })
      : legendItems;

  const widgetConfiguration = {
    name: 'composite_graph_data_processor_graph',
    type: WidgetType.o_composite_graph,
    visible: true,
    relatedData: {
      data: processedData,
      chartConfig: resolvedChartConfig,
      legendItems: resolvedLegendItems,
      customBenchmark: activeCustomBenchmark,
      ...delegated,
    },
  };

  return <WidgetSlotRenderer nestedWidget={widgetConfiguration} />;
};
